import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { toast } from "burnt";
import { useCallback, useState } from "react";
import { amp } from "src/api/amplitude";
import GetGatheringAndPhotosDocument from "src/api/graphql/__generated__/documents/GetGatheringAndPhotosDocument";
import SetGatheringPhotoModerationDocument from "src/api/graphql/__generated__/documents/SetGatheringPhotoModerationDocument";
import ModerationEvaluation from "src/api/graphql/__generated__/enums/ModerationEvaluation";
import PhotoState from "src/api/graphql/__generated__/enums/PhotoState";
import evictPhotos from "src/api/graphql/evictPhotos";
import pluralS from "src/helpers/pluralS";
import { enumNever } from "src/shared/helpers/generalHelpers";

const useSetGatheringPhotoModeration = () => {
	const [loading, setLoading] = useState(false);
	const apolloClient = useApolloClient();

	const setGatheringPhotoModeration = useCallback(
		async ({
			gatheringId,
			photoIds,
			moderation,
		}: {
			gatheringId: string;
			photoIds: string[];
			moderation: ModerationEvaluation.ManualAllow | ModerationEvaluation.ManualBlock;
		}) => {
			setLoading(true);
			try {
				switch (moderation) {
					case ModerationEvaluation.ManualAllow:
						track(amp.gathering.photoModeration.unhide, {
							numPhotos: photoIds.length,
						});
						break;
					case ModerationEvaluation.ManualBlock:
						track(amp.gathering.photoModeration.hide, {
							numPhotos: photoIds.length,
						});
						break;
					default:
						enumNever(moderation);
				}
				const { data } = await apolloClient.mutate({
					mutation: SetGatheringPhotoModerationDocument,
					variables: {
						input: {
							gatheringId,
							photoIds,
							moderation,
						},
					},
				});
				const { expectedPhotoState, action } = (() => {
					switch (moderation) {
						case ModerationEvaluation.ManualAllow:
							return {
								expectedPhotoState: PhotoState.Active,
								action: "unhide",
							};
						case ModerationEvaluation.ManualBlock:
							return {
								expectedPhotoState: PhotoState.Hidden,
								action: "hide",
							};
						default:
							return enumNever(moderation);
					}
				})();
				// These photos should have a new state that is the state that we expect
				const successfulPhotos = (data?.setGatheringPhotoModeration ?? []).filter(
					(photo) => photo.state === expectedPhotoState,
				);
				const failedPhotos = (data?.setGatheringPhotoModeration ?? []).filter(
					(photo) => photo.state !== expectedPhotoState,
				);
				if (successfulPhotos.length > 0) {
					evictPhotos(
						apolloClient.cache,
						successfulPhotos.map((photo) => photo.photoId),
					);
					// Giving backend time to process
					await new Promise((resolve) => setTimeout(resolve, 1000));
					await apolloClient.refetchQueries({
						include: [GetGatheringAndPhotosDocument],
					});
				}
				if (failedPhotos.length > 0) {
					if (photoIds.length === 1) {
						toast({
							preset: "error",
							title: `Unable to ${action} content`,
						});
					} else {
						toast({
							preset: "error",
							title: `Unable to ${action} ${failedPhotos.length} item${pluralS(
								failedPhotos.length,
							)}`,
						});
					}
				}
			} finally {
				setLoading(false);
			}
		},
		[apolloClient],
	);

	return {
		setGatheringPhotoModeration,
		loading,
	};
};
export default useSetGatheringPhotoModeration;
